import React, {useCallback, useEffect, useMemo, useState} from 'react';
import useFetchData from 'js/hooks/useFetchData/useFetchData';
import DataValidator from 'js/components/Common/DataValidator/DataValidator';

import fetchData from './fetchData';
import classes from './ServicesOfferings.module.pcss';
import Container from 'js/components/Common/Container/Container';
import Tabs from 'js/components/Templates/Services/ServicesOfferings/Tabs/Tabs';
import Content from 'js/components/Templates/Services/ServicesOfferings/Content/Content';
import MixPanelWrapper from 'js/servises/MixPanelWrapper';

interface Props {
    pageId: number
}

export type OfferingsType = {
    id?: number,
    offeringTitle: string,
    offeringButtonTitle: string,
    aboutTheService: string,
    advisoryService: {
        id?: string,
        listItem: string
    }[],
    managedService: {
        id?: string,
        listItem: string
    }[],
}

const ServicesOfferings = ({pageId}: Props) => {
    const [offerings, setOfferings] = useState<Array<OfferingsType>>(null);
    const [activeTab, setActiveTab] = useState<number>(0);
    const [activeSubTab, setActiveSubTab] = useState<'advisoryService' | 'managedService'>('advisoryService');
    const [advisoryServiceSubtitle, setAdvisoryServiceSubtitle] = useState<string>('');
    const [managedServiceSubtitle, setManagedServiceSubtitle] = useState<string>('');
    const [activeSubTitle, setActiveSubtitle] = useState<string>('');

    const {isLoading, error} = useFetchData({
        query: `${fetchData(pageId)}`,
        condition: !!pageId,
        updateData: (data: any): void => {
            setOfferings(data?.page?.ServicesTemplate?.servicesOfferings);
            setAdvisoryServiceSubtitle(data?.page?.ServicesTemplate?.advisoryServiceSubtitle);
            setManagedServiceSubtitle(data?.page?.ServicesTemplate?.managedServiceSubtitle);
        }
    });

    const activeSubContent = useMemo(() => {
        if (!offerings?.length) {
            return null;
        }
        if (activeSubTab === 'advisoryService') {
            return offerings[activeTab]?.advisoryService;
        }
        return offerings[activeTab]?.managedService;
    }, [activeSubTab, activeTab, offerings]);

    useEffect(() => {
        if (activeSubTab === 'advisoryService') {
            setActiveSubtitle(advisoryServiceSubtitle);
            return;
        }
        setActiveSubtitle(managedServiceSubtitle);
    }, [activeSubTab, advisoryServiceSubtitle, managedServiceSubtitle]);

    const sendMixTabEvent = useCallback(async (value: string) => (
        await MixPanelWrapper.TrackEvent(
            'Filter button is selected',
            {
                category: 'Services page',
                section: 'Service Offerings section',
                value
            })
    ), []);

    const sendMixCheckBoxEvent = useCallback(async (value: string) => (
        await MixPanelWrapper.TrackEvent(
            'Service checkbox is clicked',
            {
                category: 'Services page',
                section: 'Service Offerings section',
                value
            })
    ), []);

    return (
        <DataValidator data={!isLoading && !error}>
            <div className={classes.ServicesOfferings}>
                <Container additionalClass={classes.TopWrap}>
                    <Tabs activeTab={activeTab}
                          tabClickHandler={(id) => {
                          setActiveTab(id);
                          setActiveSubTab('advisoryService');
                    }}
                          offerings={offerings}
                          eventCallback={sendMixTabEvent}/>
                </Container>
                <Container additionalClass={classes.BottomWrap}>
                    <Content
                        activeSubTitle={activeSubTitle}
                        subTabClickHandler={val => setActiveSubTab(val)}
                        activeSubContent={activeSubContent}
                        activeSubTab={activeSubTab}
                        offerings={offerings}
                        eventCallback={sendMixCheckBoxEvent}
                        activeTab={activeTab} />
                </Container>
            </div>
        </DataValidator>
    );
};

export default ServicesOfferings;
